/**
 * Who can see ReturnWorx, and as what.
 *
 * Shared by the page, the nav and the router so they can't disagree.
 *
 * Read from the right places:
 *   the users/{uid} document   dbStore.stateData.user — enrollment, card
 *   the auth token claims      userStore.stateData.claims — plan and product
 *
 * (An earlier version read enrollment from userStore.stateData, which never
 * holds the user document. Enrolment only "stuck" in the browser that did it
 * and was lost at sign-out.)
 */

// Private beta: reaches the portal without the setup steps.
export const BETA_OWNERS = ['DM1', 'JC11', 'AM7', 'GM1', 'RL1', 'AS8', 'LC5', 'DC5']

// ─── Launch switch ───
// false: ReturnWorx is invisible except to BETA_OWNERS, anyone already signed
//        up (they carry the returnworx claim), and a browser that has opened
//        any page with ?rwpreview=1 (undo with ?rwpreview=0) — for testing
//        the sign-up and setup flow before launch.
// true:  every PrepWorx client sees the nav item, and /start takes sign-ups.
export const RETURNWORX_LAUNCHED = false

function previewOn() {
  try {
    const q = new URLSearchParams(window.location.search).get('rwpreview')
    if (q === '1') localStorage.setItem('rw_preview', '1')
    if (q === '0') localStorage.removeItem('rw_preview')
    return localStorage.getItem('rw_preview') === '1'
  } catch {
    return false
  }
}

/** Should this person see ReturnWorx at all yet? */
export function returnWorxVisible(uid, claims) {
  return RETURNWORX_LAUNCHED
    || BETA_OWNERS.includes(uid || '')
    || claims?.returnworx === true
    || previewOn()
}

/** Paying PrepWorx client. */
export const hasPrepWorxPlan = (claims) => (claims?.stripeRole || '') === 'premium'

/** Signed up for ReturnWorx (either way in). Set server-side, not client-writable. */
export const hasReturnWorxClaim = (claims) => claims?.returnworx === true

/** ReturnWorx without a PrepWorx plan — sees only the ReturnWorx pages. */
export const isReturnWorxOnly = (claims) => hasReturnWorxClaim(claims) && !hasPrepWorxPlan(claims)

/**
 * The portal is open: rates accepted AND a card Stripe can charge. Until
 * then the page shows the setup steps instead.
 *
 * @param {string} uid
 * @param {object} userDoc  dbStore.stateData.user
 */
export function canUseReturns(uid, userDoc) {
  // BETA_OWNERS used to short-circuit here and open the portal outright. That
  // skipped RwEnroll, so a beta owner had no accepted rate schedule and — once
  // enrollment started stamping one — no accepted Terms of Service either. The
  // portal is the thing you get for accepting the terms, so it cannot also be
  // the thing that lets you skip them. BETA_OWNERS still controls visibility
  // in returnWorxVisible(); it no longer controls access.
  //
  // `uid` is kept in the signature because three callers pass it.
  return userDoc?.returns?.active === true
}

/** Accepted the rates (may still need a card). */
export function isEnrolledInReturns(userDoc) {
  return userDoc?.returns?.enrolled === true
}
